import React, { useState, useContext, useEffect } from "react";
import Button from "~/assets/js/components/Button";
import StickyFooter from "~/assets/js/components/StickyFooter";
import AcceptTermsNotification from "./AcceptTermsNotification";
import i18n from "~/assets/js/Helpers/i18n";
import CheckoutSummary from "~/assets/js/components/CheckoutSummary";
import Checkbox from "~/assets/js/components/Forms/Checkbox";
import Cutlery from "~/assets/js/components/Cutlery";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import Headline from "~/assets/js/components/Typography/Headline";
import Comment from "./Comment";
import TimeSlotEdit from "./TimeSlotEdit";
import AddressSelected from "./AddressSelected";
import CartProducts from "./CartProducts";
import DeliverySlot from "./DeliverySlot";
import TypeOfOrder from "./TypeOfOrder";
import { DesktopGrid } from "~/assets/js/components/Layout/DesktopGrid";
import TypeOfPayment from "./TypeOfPayment";
import { ACTIONS, CheckoutContext, PAGES } from "../../CheckoutProvider";
import { AddToast } from "~/assets/js/components/Toast";
import moment from "moment";
import { getDeliveryTimes } from "~/assets/js/Api/deliveryTimesApi";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import { ensureNoContactDataIsMissing } from "~/assets/js/Helpers/util";
import { getFirstAvailableTimeslot } from "~/assets/js/Helpers/timeslot-utils";
import { omit, isEmpty } from "lodash";
import DeliveryInstructions from "./DeliveryInstructions";
import { useCartContext } from "~/assets/js/global/cartContext";
import useDeliveryTypeChangeListener from "~/assets/js/Helpers/hooks/useDeliveryTypeChangeListener";
import {
    checkForSlotErrors,
    CART_ERRORS_MAP,
} from "~/assets/js/global/cartErrorsHandler";
import useGroupOrder from "~/assets/js/global/useGroupOrder";

const OrderOverview = () => {
    const cookie = useAreaCookie();
    const isDelivery = cookie.area && cookie.area.type === "delivery";
    const { isGroupOrder } = useGroupOrder();
    const context = useContext(CheckoutContext);
    const { state: contextState, dispatch } = context;
    const { cart, actions, requiresPayment } = useCartContext();
    const { isDesktop } = useViewport();
    const [state, setState] = useState({
        terms: false,
        showTermsNotif: false,
        loading: false,
        timeSlotTaken: false,
        comment: cart?.shipping?.comment || "",
        nextDeliverySlot: {},
        deliveryInstructions: {
            contactless_delivery: cart.shipping?.contactless_delivery || false,
            give_porter: cart.shipping?.give_porter || false,
            delivery_phone_call: cart.shipping?.delivery_phone_call || false,
            no_doorbell: cart.shipping?.no_doorbell || false,
        },
    });

    const handleComment = async (comment) => {
        setState({ ...state, comment });
    };

    const handleDeliveryInstructions = (value) => {
        setState({
            ...state,
            deliveryInstructions: { ...state.deliveryInstructions, ...value },
        });
    };

    useDeliveryTypeChangeListener({
        onDeliveryTypeChangeCb: () =>
            dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.TIME_SLOT_SELECT,
            }),
        onLocationChangeCb: () =>
            dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.PAYMENT_SELECT,
            }),
    });

    useEffect(() => {
        const redirectToPaymentSelectOnPaymentFail = () => {
            const { paymentErrorReason } = reactConfig;

            if (paymentErrorReason) {
                dispatch({
                    type: ACTIONS.CHANGE_PAGE,
                    payload: PAGES.PAYMENT_SELECT,
                });
                history.pushState({}, document.title, "/warenkorb/");
            }
        };
        redirectToPaymentSelectOnPaymentFail();
    }, []);

    useEffect(() => {
        if (!cart.errors) return;
        const handleCartErrors = async (errors) => {
            const hasSlotErrors = checkForSlotErrors(errors);
            if (
                hasSlotErrors &&
                errors.includes(CART_ERRORS_MAP.SLOT_TIMEOUT)
            ) {
                await handleSlotTimeoutError();
                return;
            }

            // Either handle with popup or redirect to timeslot picker section
            if (hasSlotErrors) {
                fetchDeliveryTimes();
            }
        };

        handleCartErrors(cart.errors);
    }, [cart?.errors]);

    const toggleTerms = () => {
        setState({
            ...state,
            terms: !state.terms,
            showTermsNotif: false,
        });
    };

    const handleSlotTimeoutError = async () => {
        // Get latest delivery times...
        const deliveryTimes = await getDeliveryTimes();
        // Check if today any free slots left
        const hasTimeslotToday = checkIfAnyTimeSlotsToday(deliveryTimes);

        if (!hasTimeslotToday) {
            return dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.TIME_SLOT_SELECT,
            });
        }

        /**
         * There are slots today, find the first one that matches
         * and suggest it to the customer via popup
         */
        const today = moment().format("YYYY-MM-DD");
        const todaySlots = deliveryTimes[today];
        const nextDeliverySlot = getFirstAvailableTimeslot(todaySlots);

        /**
         * Daily slots are also taken
         */
        if (!nextDeliverySlot) {
            return dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.TIME_SLOT_SELECT,
            });
        }

        setState({
            ...state,
            timeSlotTaken: true,
            nextDeliverySlot: nextDeliverySlot,
        });
    };

    const checkIfContactDataIsMissing = () => {
        const { orderAddress } = contextState;
        const { billing_address, shipping_address } = orderAddress;

        const isBillingEmpty = !ensureNoContactDataIsMissing(billing_address);
        const isShippingEmpty = !ensureNoContactDataIsMissing(
            omit(shipping_address, ["phone"])
        );

        return isBillingEmpty || isShippingEmpty;
    };

    const handleSubmitOrder = async () => {
        if (!state.terms) {
            return setState({ ...state, showTermsNotif: true });
        }

        setState({ ...state, loading: true });

        const isContactDataMissing = checkIfContactDataIsMissing();

        if (isContactDataMissing) {
            dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.CONTACT_DETAILS,
            });
            return;
        }

        const requiredPaymentButNotProvided =
            requiresPayment && isEmpty(cart.payment);

        if (requiredPaymentButNotProvided) {
            dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.PAYMENT_SELECT,
            });
            return;
        }

        const response = await actions.checkoutCart(
            state.deliveryInstructions,
            state.comment
        );

        if (!response || !response.ok) {
            setState({ ...state, loading: false });
            const hasTimeslotErrors = checkForSlotErrors(response.cart?.errors);
            if (!hasTimeslotErrors) {
                dispatch(
                    AddToast("danger", i18n.t("orderConfirmation.submitFailed"))
                );
            }
            return;
        }

        // Redirect to correct page after submitting order.
        window.location.href = response.redirect_to;
    };

    const checkSlotTaken = (deliveryTimes) => {
        const contextTimeSlot = contextState.timeSlot;
        let taken = false;
        let nextDeliverySlot;
        Object.keys(deliveryTimes).map((key) => {
            if (contextTimeSlot.deliveryDay === key) {
                deliveryTimes[key].map((deliveryTime) => {
                    if (contextTimeSlot.begin === deliveryTime.begin) {
                        nextDeliverySlot = getFirstAvailableTimeslot(
                            deliveryTimes[key],
                            contextTimeSlot.valid_till
                        );

                        if (!deliveryTime.free) {
                            taken = true;
                        }
                    }
                });
            }
        });

        setState({
            ...state,
            timeSlotTaken: true,
            nextDeliverySlot: nextDeliverySlot,
        });
        return taken;
    };

    const editTimeSlotClose = () => {
        setState({ ...state, timeSlotTaken: false, loading: false });
    };

    const checkIfAnyTimeSlotsToday = (deliveryTimes) => {
        const today = moment().format("YYYY-MM-DD");
        // eslint-disable-next-line no-unused-vars
        const [firstAvailableDay, ..._rest] = Object.keys(deliveryTimes);

        return today === firstAvailableDay;
    };

    const fetchDeliveryTimes = async () => {
        const deliveryTimes = await getDeliveryTimes();

        // Determine if we need to open a popup to notify user of taken slot
        const isSlotTaken = checkSlotTaken(deliveryTimes);

        //Check if timeslots for today are available and redirect back to timeslot
        if (isSlotTaken) {
            const hasTimeslotsToday = checkIfAnyTimeSlotsToday(deliveryTimes);

            if (!hasTimeslotsToday) {
                return context.dispatch({
                    type: ACTIONS.CHANGE_PAGE,
                    payload: PAGES.TIME_SLOT_SELECT,
                });
            }
        }

        return isSlotTaken;
    };

    return (
        <>
            <DesktopGrid
                leftSide={
                    <>
                        <Headline size={5} resizable>
                            {i18n.t("overview.title")}
                        </Headline>
                        <TimeSlotEdit
                            slotTaken={state.timeSlotTaken}
                            nextDeliverySlot={state.nextDeliverySlot}
                            handleClose={editTimeSlotClose}
                        />
                        {!isDesktop && (
                            <Button
                                onClick={handleSubmitOrder}
                                label={i18n.t("overview.next")}
                                loading={state.loading}
                            />
                        )}
                        <AcceptTermsNotification open={state.showTermsNotif} />
                        {!isDesktop && (
                            <Checkbox
                                label={<Terms />}
                                value={state.terms}
                                onChange={toggleTerms}
                            />
                        )}
                        {isDelivery && (
                            <DeliveryInstructions
                                handleOnChange={handleDeliveryInstructions}
                                defaultValues={state.deliveryInstructions}
                            />
                        )}
                        <Comment
                            comment={state.comment}
                            handleComment={handleComment}
                            isDelivery={isDelivery}
                        />
                        <Cutlery />
                        <CartProducts cartItems={cart.items} />
                        <TypeOfOrder edit={!isGroupOrder} />
                        <DeliverySlot edit={!isGroupOrder} />

                        {isDelivery && (
                            <AddressSelected
                                title="overview.delivery"
                                address={cart.shipping}
                                edit={!isGroupOrder}
                            />
                        )}
                        <AddressSelected
                            title="overview.billing"
                            address={cart.billing}
                            edit
                        />
                        <TypeOfPayment edit />

                        {!isDesktop && (
                            <CheckoutSummary
                                handleDesktopMoveForward={handleSubmitOrder}
                                desktopForwardLabel={i18n.t("overview.next")}
                                loadingForwardButton={state.loading}
                                termsComponent={
                                    <Checkbox
                                        label={<Terms />}
                                        value={state.terms}
                                        onChange={toggleTerms}
                                    />
                                }
                            />
                        )}
                    </>
                }
                rightSide={
                    isDesktop && (
                        <CheckoutSummary
                            handleDesktopMoveForward={handleSubmitOrder}
                            desktopForwardLabel={i18n.t("overview.next")}
                            loadingForwardButton={state.loading}
                            forwardButtonDisabled={!state.terms}
                            termsComponent={
                                <Checkbox
                                    label={<Terms />}
                                    value={state.terms}
                                    onChange={toggleTerms}
                                />
                            }
                        />
                    )
                }
            />

            <StickyFooter
                hidden={!state.terms}
                disabled={!state.terms}
                CTA="overview.next"
                totals={cart.value_gross}
                onClick={handleSubmitOrder}
                loading={state.loading}
            />
        </>
    );
};

const Terms = () => {
    const linkAGB = "https://pottsalat.de/agb/";
    const linkPrivacy = "https://pottsalat.de/datenschutz/";
    const linkCancellation = "https://pottsalat.de/widerrufsbelehrung/";

    return (
        <span>
            {i18n.t("overview.terms.firstPart")}
            <a
                href={linkAGB}
                className="gradient-right-text"
                target="_blank"
                rel="noreferrer"
            >
                {i18n.t("overview.terms.linkPartAGB")}
            </a>
            {i18n.t("overview.terms.linkClip1")}
            <a
                href={linkPrivacy}
                className="gradient-right-text"
                target="_blank"
                rel="noreferrer"
            >
                {i18n.t("overview.terms.linkPartPrivacy")}
            </a>
            {i18n.t("overview.terms.linkClip2")}
            <a
                href={linkCancellation}
                className="gradient-right-text"
                target="_blank"
                rel="noreferrer"
            >
                {i18n.t("overview.terms.linkPartCancellation")}
            </a>
            {i18n.t("overview.terms.lastPart")}
        </span>
    );
};

export default OrderOverview;
